import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Alert, createTheme, GlobalStyles, alpha, lighten, darken } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { NotFound } from '@/components/NotFound';

// React Router future flags to suppress deprecation warnings
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};
import { createMelpTheme } from '@melp/shared-theme';
import { LoadingIndicator } from '@/components/shared/LoadingIndicator';
import { MainLayout } from '@/components/MainLayout';
import { CommandPalette } from '@/components/CommandPalette';
import { useAuth } from '@/hooks/useAuth';
import { useEmailStore, buildEmailListCacheKey } from '@/store/emailStore';
import { JmapClient } from '@/services/jmapClient';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { usePreferences } from '@/contexts/PreferencesContext';
import { ToastProvider, useToast } from '@/contexts/ToastContext';
import { JmapProvider } from '@/contexts/JmapContext';
import { getPreferences, getBranding, fetchProfileAvatar } from '@/services/tenantClient';
import { useI18n } from './i18n';
import { ProductTourDialog } from '@/components/Help/ProductTourDialog';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import LoginPage from '@/pages/LoginPage';

const Mailbox = lazy(() => import('@/pages/Mailbox').then((module) => ({ default: module.Mailbox })));
const Contacts = lazy(() => import('@/pages/Contacts').then((module) => ({ default: module.Contacts })));
const Calendar = lazy(() => import('@/pages/Calendar').then((module) => ({ default: module.Calendar })));
const CalendarEventDetail = lazy(() =>
  import('@/pages/CalendarEventDetail').then((module) => ({ default: module.CalendarEventDetail }))
);
const Drive = lazy(() => import('@/pages/Drive').then((module) => ({ default: module.Drive })));
const Settings = lazy(() => import('@/pages/Settings').then((module) => ({ default: module.Settings })));

// React Query defaults tuned for an email client:
// - 5 min staleTime: revisiting a page (Contacts → Calendar → Contacts)
//   reads from cache instead of refetching. The previous default of 0
//   meant every page-mount fired a fresh GET — exactly the "fucked up"
//   waterfall the user saw on every module switch.
// - refetchOnWindowFocus off: tab-switch should not pummel the server
//   for data that's already on screen.
// - retry once: server hiccup → one quick retry, not the default 3.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const NotificationsBridge = ({ token, accountId }: { token: string | null; accountId: string | null }) => {
  const { showInfo, showBrand } = useToast();
  const incrementNewMailCount = useEmailStore((state) => state.incrementNewMailCount);
  const incrementNewCalendarCount = useEmailStore((state) => state.incrementNewCalendarCount);
  const mergeNewerEmails = useEmailStore((state) => state.mergeNewerEmails);

  // Refs so the SSE handler always sees current values without re-subscribing.
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // When the tab regains focus (browsers often pause SSE in background tabs),
  // delta-load anything that arrived while we were idle.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const acct = accountIdRef.current;
      const tok = tokenRef.current;
      if (!acct || !tok) return;
      const state = useEmailStore.getState();
      const inbox = state.mailboxes.find((m) => m.role === 'inbox');
      if (!inbox) return;
      const inboxKey = buildEmailListCacheKey({ mailbox: inbox.id });
      const cachedInbox = state.emailListCache[inboxKey];
      const visibleInbox = state.selectedMailbox === inbox.id ? state.emails : [];
      const candidates = [...(cachedInbox?.emails ?? []), ...visibleInbox];
      const latestMs = candidates.reduce((max, e) => {
        const t = e.receivedAt ? new Date(e.receivedAt).getTime() : 0;
        return t > max ? t : max;
      }, 0);
      if (latestMs === 0) return;
      const afterIso = new Date(latestMs - 5000).toISOString();
      const client = new JmapClient(tok);
      client
        .getEmailsAfter(acct, inbox.id, afterIso, 30)
        .then((newer) => {
          if (newer.length === 0) return;
          mergeNewerEmails(inbox.id, newer);
          incrementNewMailCount(newer.length);
        })
        .catch((err) => console.warn('Visibility delta load failed', err));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mergeNewerEmails, incrementNewMailCount]);

  // Pull mailboxes/emails/cache lazily inside the handler — calling
  // useEmailStore.getState() avoids re-subscribing the SSE on every store change.
  useNotificationStream(token, {
    onMail: (payload) => {
      const lines = ['New mail'];
      if (payload.sender) lines.push(`From: ${payload.sender}`);
      if (payload.subject) lines.push(`Subject: ${payload.subject}`);
      showBrand(lines.join('\n'));
      incrementNewMailCount(1);

      const acct = accountIdRef.current;
      if (!token || !acct) return;

      // Resolve the inbox mailbox. Some JMAP servers report role lowercase
      // ('inbox'), some title-case — match both to be safe.
      const state = useEmailStore.getState();
      const inbox = state.mailboxes.find(
        (m) => (m.role || '').toLowerCase() === 'inbox'
      );
      if (!inbox) return;

      // 1. Sidebar badge: bump inbox unread/total immediately so the user
      //    sees the count change the moment the toast appears. If the later
      //    delta-fetch tells us the message routed elsewhere (sieve filter,
      //    spam), the count will reconcile on next focus / mailbox refresh.
      const store = useEmailStore.getState();
      store.bumpMailboxCounts(inbox.id, { unread: 1, total: 1 });

      // 2. Delta-fetch the new mail and merge on top. NEVER clear the cache
      //    or trigger a full Email/query — that throws away in-memory state
      //    (scroll position, selection, etc.) and is wasteful when we know
      //    exactly what arrived. mergeNewerEmails prepends to the cache and
      //    to the visible list (if the user is on inbox) without touching
      //    state.emails identity for anything else.
      //
      //    James's Lucene index can lag the webhook by 50ms–10s, so we
      //    retry the delta a few times. First success ends the chain.
      const inboxKey = buildEmailListCacheKey({ mailbox: inbox.id });
      const cachedInbox = state.emailListCache[inboxKey];
      const visibleInbox = state.selectedMailbox === inbox.id ? state.emails : [];
      const candidates = [...(cachedInbox?.emails ?? []), ...visibleInbox];
      const knownIds = new Set(candidates.map((e) => e.id));
      const latestMs = candidates.reduce((max, e) => {
        const t = e.receivedAt ? new Date(e.receivedAt).getTime() : 0;
        return t > max ? t : max;
      }, 0);
      const afterIso = new Date(latestMs > 0 ? latestMs - 5000 : Date.now() - 5 * 60_000).toISOString();
      const client = new JmapClient(token);
      const tryDelays = [0, 1500, 4000, 10_000];
      let resolved = false;
      const attempt = async (i: number) => {
        if (resolved) return;
        try {
          const newer = await client.getEmailsAfter(acct, inbox.id, afterIso, 30);
          const trulyNew = newer.filter((e: { id: string }) => !knownIds.has(e.id));
          if (trulyNew.length > 0) {
            mergeNewerEmails(inbox.id, trulyNew);
            trulyNew.forEach((e: { id: string }) => knownIds.add(e.id));
            resolved = true;
            return;
          }
        } catch (err) {
          console.warn('Delta load failed', err);
        }
        if (i + 1 < tryDelays.length) {
          setTimeout(() => attempt(i + 1), tryDelays[i + 1]);
        }
      };
      void attempt(0);
    },
    onCalendar: (payload) => {
      const summary = payload.summary ? `: ${payload.summary}` : '';
      if (payload.type === 'calendar.event_reminder') {
        showInfo(`Event reminder${summary}`);
        return;
      }
      showInfo(`New calendar event${summary}`);
      incrementNewCalendarCount(1);
      // Tell the Calendar page to delta-fetch the single new event by id
      // and merge it into local state. This replaces the previous full
      // visible-range refetch on every SSE event — that path threw away
      // any in-memory selection / drag state and was wasteful for a
      // change that touches one event.
      if (payload.eventId && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('webmail:calendar-event-arrived', {
          detail: { eventId: payload.eventId },
        }));
      }
    },
  });

  return null;
};

// Restores the URL the user was trying to reach before the unauthenticated
// bounce to /login. Lives inside the <Router> so we can use useNavigate(),
// which (unlike window.history.replaceState) actually re-resolves the
// matched route and mounts the correct page.
const PostLoginRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem('webmail:postLoginRedirect');
    } catch {}
    if (!saved) return;
    try { sessionStorage.removeItem('webmail:postLoginRedirect'); } catch {}
    if (saved !== window.location.pathname + window.location.search) {
      navigate(saved, { replace: true });
    }
  }, [navigate]);
  return null;
};

// Listens for session-expired events dispatched by apiClient when token refresh
// fails. Surfaces a single warning toast so the user knows why they were just
// signed out, instead of being silently bounced to the login screen.
const SessionExpiredBridge = () => {
  const { showWarning } = useToast();
  useEffect(() => {
    const onExpired = () => {
      showWarning('Your session has expired. Please sign in again.');
    };
    window.addEventListener('webmail:session-expired', onExpired);
    return () => window.removeEventListener('webmail:session-expired', onExpired);
  }, [showWarning]);
  return null;
};

function App() {
  const { token, isAuthenticated, isInitialized } = useAuth();
  const { preferences, setPreferences } = usePreferences();
  const { t } = useI18n();
  const { setMailboxes, selectMailbox, selectedMailbox, mailboxes, setAccountProfile, resetStore } = useEmailStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [jmapClient, setJmapClient] = useState<JmapClient | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const authWasActive = useRef(false);

  // Global ⌘K / Ctrl+K opens the command palette. Only when authenticated —
  // otherwise the login screen owns the keyboard. Also responds to a
  // window event so the header's search button can open the palette without
  // prop-drilling state through every page.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onKey = (e: KeyboardEvent) => {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (!isModK) return;
      e.preventDefault();
      setCommandOpen((v) => !v);
    };
    const onOpen = () => setCommandOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('webmail:open-command-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('webmail:open-command-palette', onOpen);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      authWasActive.current = true;
      return;
    }
    if (isInitialized && authWasActive.current) {
      resetStore();
      setAccountId(null);
      setJmapClient(null);
      setPrefsLoaded(false);
      setError(null);
      setLoading(false);
      authWasActive.current = false;
    }
  }, [isAuthenticated, isInitialized, resetStore, setAccountId, setError, setJmapClient, setLoading, setPrefsLoaded]);


  useEffect(() => {
    async function loadPrefs() {
      if (!token) return;

      // Prevent loading multiple times
      if (prefsLoaded) {
        console.log('[Webmail] Preferences already loaded, skipping');
        return;
      }

      console.log('[Webmail] Loading preferences and branding from server...');
      try {
        const [prefsResp, brandingResp] = await Promise.all([
          getPreferences(token),
          getBranding(token)
        ]);
        console.log('[Webmail] Preferences response:', prefsResp);
        console.log('[Webmail] Branding response:', brandingResp);

        // Check if branding response has any actual branding data
        const hasValidBranding = brandingResp && (
          brandingResp.productName ||
          brandingResp.primaryColor ||
          brandingResp.secondaryColor ||
          brandingResp.logoDataUrl ||
          brandingResp.faviconDataUrl
        );

        console.log('[Webmail] Has valid branding:', hasValidBranding);

        setPreferences({
          theme: (prefsResp.theme as any) || 'system',
          language: prefsResp.language || 'en',
          timezone: prefsResp.timezone || 'UTC',
          branding: hasValidBranding ? brandingResp : undefined
        });
        console.log('[Webmail] Preferences and branding loaded and persisted successfully');
      } catch (e) {
        // Non-fatal; fall back to local defaults (including localStorage branding)
        console.error('[Webmail] Preferences/branding fetch failed:', e);
        console.log('[Webmail] Using branding from localStorage if available');
      } finally {
        setPrefsLoaded(true);
      }
    }
    loadPrefs();
  }, [token, setPreferences, prefsLoaded]);

  const resolvedMode = useMemo(() => {
    const mode = preferences.theme;
    if (mode === 'system') {
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return mode;
  }, [preferences.theme]);

  const themed = useMemo(() => {
    const baseTheme = createMelpTheme(resolvedMode);

    // Helper to validate hex color
    const isValidHexColor = (color: string) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);

    // Get brand colors with validation
    const rawPrimary = preferences.branding?.primaryColor as string;
    const rawSecondary = preferences.branding?.secondaryColor as string;
    const primary = (rawPrimary && isValidHexColor(rawPrimary)) ? rawPrimary : baseTheme.palette.primary.main;
    const secondary = (rawSecondary && isValidHexColor(rawSecondary)) ? rawSecondary : (baseTheme.palette.secondary.main || primary);

    // Generate primary color variants from brand color
    const primaryLight = lighten(primary, 0.3);
    const primaryDark = darken(primary, 0.2);

    // Generate action hover colors based on primary (subtle tint)
    const actionHover = resolvedMode === 'dark'
      ? alpha(primary, 0.08)
      : alpha(primary, 0.04);
    const actionSelected = resolvedMode === 'dark'
      ? alpha(primary, 0.16)
      : alpha(primary, 0.08);

    const brandTheme = createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          ...baseTheme.palette.primary,
          main: primary,
          light: primaryLight,
          dark: primaryDark,
        },
        secondary: { ...baseTheme.palette.secondary, main: secondary },
        mode: resolvedMode,
        action: {
          ...baseTheme.palette.action,
          hover: actionHover,
          selected: actionSelected,
        },
      },
      components: {
        ...baseTheme.components,
        MuiButton: {
          ...(baseTheme.components?.MuiButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '2px',
              },
            },
          },
        },
        MuiIconButton: {
          ...(baseTheme.components?.MuiIconButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiIconButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiIconButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '2px',
              },
            },
          },
        },
        MuiListItemButton: {
          ...(baseTheme.components?.MuiListItemButton as any || {}),
          styleOverrides: {
            ...(baseTheme.components?.MuiListItemButton?.styleOverrides as any || {}),
            root: {
              ...(baseTheme.components?.MuiListItemButton?.styleOverrides as any)?.root || {},
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: primary,
                outlineOffset: '-2px',
              },
            },
          },
        },
      },
    });
    return brandTheme;
  }, [resolvedMode, preferences.branding]);

  // Apply favicon and document title when branding changes
  useEffect(() => {
    console.log('[Webmail] Branding effect triggered, branding:', preferences.branding);
    if (!preferences.branding) {
      console.log('[Webmail] No branding available, using defaults');
      return;
    }

    // Apply favicon
    if (preferences.branding.faviconDataUrl && typeof document !== 'undefined') {
      console.log('[Webmail] Applying favicon:', preferences.branding.faviconDataUrl?.substring(0, 50) + '...');
      let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = preferences.branding.faviconDataUrl;
    }

    // Apply product name to document title
    if (typeof document !== 'undefined') {
      const name = preferences.branding.productName || 'MelpApp Mail';
      console.log('[Webmail] Applying document title:', name);
      document.title = name;
    }

    // Log other branding properties
    if (preferences.branding.logoDataUrl) {
      console.log('[Webmail] Logo data URL available:', preferences.branding.logoDataUrl?.substring(0, 50) + '...');
    }
    if (preferences.branding.primaryColor) {
      console.log('[Webmail] Primary color:', preferences.branding.primaryColor);
    }
    if (preferences.branding.secondaryColor) {
      console.log('[Webmail] Secondary color:', preferences.branding.secondaryColor);
    }
  }, [preferences.branding]);

  // Tour trigger via global event + first-run auto-show
  useEffect(() => {
    const handler = () => setTourOpen(true);
    window.addEventListener('webmail-show-tour', handler as any);
    return () => window.removeEventListener('webmail-show-tour', handler as any);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      // Don't show tour if embedded from admin portal (token in URL)
      const urlParams = new URLSearchParams(window.location.search);
      const isEmbedded = urlParams.has('token');
      if (isEmbedded) return;

      const seen = localStorage.getItem('webmail_tour_seen');
      if (!seen) setTourOpen(true);
    } catch {}
  }, [isAuthenticated]);

  // When auth bootstrap finishes and the user is signed out, save the URL
  // they were trying to reach (e.g. /calendar/abc123) so we can return them
  // to it after login. Without this, deep links always bounce to /mail.
  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        const here = window.location.pathname + window.location.search;
        const isLoginPath = window.location.pathname === '/login';
        const isAuthSpecial = here === '/' || here === '';
        if (!isLoginPath && !isAuthSpecial) {
          try {
            sessionStorage.setItem('webmail:postLoginRedirect', here);
          } catch {}
        }
        if (!isLoginPath) {
          window.history.replaceState({}, '', '/login');
        }
      }
    }
  }, [isAuthenticated, isInitialized]);

  // The post-login URL restore is handled inside the Router subtree by
  // <PostLoginRedirect /> below. Doing it here with window.history.* would
  // change the address bar but NOT update React Router's matched route, so
  // the wrong page would stay mounted. Keeping that effect Router-internal
  // means we can use useNavigate() and the route resolves correctly.

  useEffect(() => {
    async function initializeJmap() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const client = new JmapClient(token);
        setJmapClient(client);

        // Get JMAP session
        const sessionData = await client.getSession();

        // Get primary account ID
        const primaryAccountId = sessionData.primaryAccounts?.['urn:ietf:params:jmap:mail']
          || Object.keys(sessionData.accounts)[0];

        if (!primaryAccountId) {
          throw new Error('No mail account found in JMAP session');
        }

        setAccountId(primaryAccountId);

        // Fetch mailboxes
        const mailboxes = await client.getMailboxes(primaryAccountId);
        setMailboxes(mailboxes);

        try {
          const config = await client.getAccountConfig();
          let avatarUrl: string | null = null;
          try {
            const avatarBlob = await fetchProfileAvatar(token);
            if (avatarBlob) {
              avatarUrl = URL.createObjectURL(avatarBlob);
            }
          } catch {}
          setAccountProfile(config.email || null, config.displayName || null, avatarUrl);
        } catch {
          const username = sessionData?.username || null;
          setAccountProfile(username, null, null);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Failed to initialize JMAP:', err);
        setError(err.message || 'Failed to connect to mail server');
        setLoading(false);
      }
    }

    if (isAuthenticated && token) {
      initializeJmap();
    }
  }, [isAuthenticated, token, setMailboxes]);

  useEffect(() => {
    if (selectedMailbox) return;
    const inbox = mailboxes.find((m: any) => m.role === 'inbox');
    if (inbox) {
      selectMailbox(inbox.id);
    }
  }, [mailboxes, selectedMailbox, selectMailbox]);

  // Show loading while auth is initializing
  if (!isInitialized) {
    return <LoadingIndicator size="large" fullHeight />;
  }

  // Show webmail login screen when not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={themed}>
        <CssBaseline />
        <LoginPage />
      </ThemeProvider>
    );
  }

  if (loading) {
    return <LoadingIndicator size="large" fullHeight />;
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" p={3}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>{t('app.connectionError.title')}</Typography>
          <Typography variant="body2">{error}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 2 }}>
            {t('app.connectionError.hint')}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={themed}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <GlobalStyles styles={{
          body: {
            backgroundColor: themed.palette.background.default,
            color: themed.palette.text.primary,
            transition: 'background-color 0.2s ease, color 0.2s ease',
          },
        }} />
        <ToastProvider>
          <JmapProvider token={token ?? null} client={jmapClient} accountId={accountId}>
          <ErrorBoundary>
            <Router future={routerFutureConfig}>
              <Suspense fallback={<LoadingIndicator size="large" fullHeight />}>
                <Routes>
                  <Route path="/login" element={<Navigate to="/mail" replace />} />
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<Navigate to="/mail" replace />} />
                    <Route path="/mail" element={
                      jmapClient && accountId ? (
                        <Mailbox token={token!} accountId={accountId} jmapClient={jmapClient} />
                      ) : null
                    } />
                    <Route path="/mail/:folder" element={
                      jmapClient && accountId ? (
                        <Mailbox token={token!} accountId={accountId} jmapClient={jmapClient} />
                      ) : null
                    } />
                    <Route path="/mail/:folder/:emailId" element={
                      jmapClient && accountId ? (
                        <Mailbox token={token!} accountId={accountId} jmapClient={jmapClient} />
                      ) : null
                    } />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route path="/contacts/:contactId" element={<Contacts />} />
                    <Route path="/calendar" element={
                      accountId ? (
                        <Calendar token={token!} accountId={accountId} />
                      ) : null
                    } />
                    <Route path="/calendar/:eventId" element={
                      accountId ? (
                        <CalendarEventDetail token={token!} accountId={accountId} />
                      ) : null
                    } />
                    <Route path="/drive" element={
                      accountId ? (
                        <Drive token={token!} accountId={accountId} />
                      ) : null
                    } />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/:tab" element={<Settings />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
              {/* CommandPalette uses useNavigate() and must live inside the
                  Router context. Mounting it outside threw on render —
                  "useNavigate() may be used only in the context of a
                  <Router> component." */}
              {isAuthenticated && (
                <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
              )}
              {/* Restores the saved pre-login URL exactly once on first render
                  after auth. Inside <Router> so navigate() re-resolves routes. */}
              {isAuthenticated && <PostLoginRedirect />}
            </Router>
            {isAuthenticated && <NotificationsBridge token={token ?? null} accountId={accountId} />}
            <SessionExpiredBridge />
            <ProductTourDialog open={tourOpen} onClose={() => setTourOpen(false)} />
          </ErrorBoundary>
          </JmapProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
